import {
    Button,
    Checkbox,
    Select,
    Textarea,
    TextInput,
} from '@mantine/core';
import { defineMessages, useIntl } from 'react-intl';

import useGetCurrentEntityId from 'hooks/useGetCurrentEntityId';
import { useParishBookPersonContext } from '../ParishBookPersonContext';

import styles from './ParishBookPersonForm.module.scss';
import { useGetParishBookCategoriesQuery } from 'api/parishBookEndpoints';
import {
    CONTACT_PERSON_DESCRIPTION_MAX_LENGTH,
    CONTACT_PERSON_TEXT_MAX_LENGTH,
    isEmailValid,
    isPhoneNumberValid,
    sanitizePhoneNumber,
} from 'utils/validationUtils';

interface Props {
    onSubmit: () => void;
    isLoading: boolean;
    isSubmitDisabled: boolean;
    hideActions?: boolean;
}

const ParishBookPersonForm = ({
    onSubmit,
    isLoading,
    isSubmitDisabled,
    hideActions = false,
}: Props) => {
    const intl = useIntl();
    const { values, setValues } = useParishBookPersonContext();
    const currentEntityId = useGetCurrentEntityId();
    const { data: categories, isFetching: isFetchingCategories } = useGetParishBookCategoriesQuery(
        currentEntityId ?? '',
        { skip: !currentEntityId, refetchOnMountOrArgChange: true },
    );

    return (
        <div className={styles.container}>
            <TextInput
                id="parish-person-firstName"
                label={intl.formatMessage(messages.firstName)}
                value={values.firstName}
                onChange={(e) => { setValues({ firstName: e.target.value }); }}
                error={values.firstName.trim().length > CONTACT_PERSON_TEXT_MAX_LENGTH ? intl.formatMessage(messages.textTooLong, { max: CONTACT_PERSON_TEXT_MAX_LENGTH }) : undefined}
                maxLength={CONTACT_PERSON_TEXT_MAX_LENGTH + 1}
                required
                radius="md"
            />
            <TextInput
                id="parish-person-lastName"
                label={intl.formatMessage(messages.lastName)}
                value={values.lastName}
                onChange={(e) => { setValues({ lastName: e.target.value }); }}
                error={values.lastName.trim().length > CONTACT_PERSON_TEXT_MAX_LENGTH ? intl.formatMessage(messages.textTooLong, { max: CONTACT_PERSON_TEXT_MAX_LENGTH }) : undefined}
                maxLength={CONTACT_PERSON_TEXT_MAX_LENGTH + 1}
                required
                radius="md"
            />
            <Select
                id="parish-person-category"
                label={intl.formatMessage(messages.category)}
                value={values.categoryId || null}
                onChange={(value) => { setValues({ categoryId: value ?? '' }); }}
                disabled={isFetchingCategories}
                data={(categories ?? [])
                    .map((cat) => ({
                        value: String(cat.id),
                        label: cat.name,
                    }))}
                placeholder={intl.formatMessage(messages.selectCategory)}
                required
                radius="md"
                searchable
                clearable={false}
                comboboxProps={{ withinPortal: false }}
            />
            <TextInput
                id="parish-person-availability"
                label={intl.formatMessage(messages.availability)}
                value={values.availability ?? ''}
                onChange={(e) => { setValues({ availability: e.target.value }); }}
                error={(values.availability ?? '').trim().length > CONTACT_PERSON_TEXT_MAX_LENGTH ? intl.formatMessage(messages.textTooLong, { max: CONTACT_PERSON_TEXT_MAX_LENGTH }) : undefined}
                maxLength={CONTACT_PERSON_TEXT_MAX_LENGTH + 1}
                radius="md"
            />
            <TextInput
                id="parish-person-phoneNumber"
                label={intl.formatMessage(messages.phoneNumber)}
                value={values.phoneNumber ?? ''}
                onChange={(e) => { setValues({ phoneNumber: sanitizePhoneNumber(e.target.value) }); }}
                error={!isPhoneNumberValid(values.phoneNumber ?? '') ? intl.formatMessage(messages.invalidPhoneNumber) : undefined}
                radius="md"
            />
            <TextInput
                id="parish-person-email"
                label={intl.formatMessage(messages.email)}
                value={values.email ?? ''}
                onChange={(e) => { setValues({ email: e.target.value }); }}
                error={!isEmailValid(values.email ?? '') ? intl.formatMessage(messages.invalidEmail) : undefined}
                radius="md"
            />
            <Textarea
                id="parish-person-description"
                label={intl.formatMessage(messages.description)}
                value={values.description ?? ''}
                onChange={(e) => { setValues({ description: e.target.value }); }}
                error={(values.description ?? '').trim().length > CONTACT_PERSON_DESCRIPTION_MAX_LENGTH ? intl.formatMessage(messages.descriptionTooLong, { max: CONTACT_PERSON_DESCRIPTION_MAX_LENGTH }) : undefined}
                maxLength={CONTACT_PERSON_DESCRIPTION_MAX_LENGTH + 1}
                rows={4}
                radius="md"
            />
            <div className={styles.checkboxContainer}>
                <Checkbox
                    id="parish-person-visibleForGuests"
                    label={intl.formatMessage(messages.visibleForGuests)}
                    checked={values.visibleForGuests ?? false}
                    onChange={(event) => setValues({ visibleForGuests: event.currentTarget.checked })}
                    radius="sm"
                />
            </div>
            {!hideActions && (
                <div className={styles.actions}>
                    <Button onClick={onSubmit} disabled={isLoading || isSubmitDisabled} radius="md">
                        {intl.formatMessage(messages.save)}
                    </Button>
                </div>
            )}
        </div>
    );
};

const messages = defineMessages({
    firstName: {
        id: 'parishBook.person.firstName',
        defaultMessage: 'Imię',
    },
    lastName: {
        id: 'parishBook.person.lastName',
        defaultMessage: 'Nazwisko',
    },
    category: {
        id: 'parishBook.person.category',
        defaultMessage: 'Rola lub funkcja',
    },
    selectCategory: {
        id: 'parishBook.person.selectCategory',
        defaultMessage: 'Wybierz rolę lub funkcję',
    },
    availability: {
        id: 'parishBook.person.availability',
        defaultMessage: 'Dostępność',
    },
    phoneNumber: {
        id: 'parishBook.person.phoneNumber',
        defaultMessage: 'Numer telefonu',
    },
    email: {
        id: 'parishBook.person.email',
        defaultMessage: 'Email',
    },
    description: {
        id: 'parishBook.person.description',
        defaultMessage: 'Opis',
    },
    visibleForGuests: {
        id: 'parishBook.person.visibleForGuests',
        defaultMessage: 'Widoczne dla parafian',
    },
    save: {
        id: 'parishBook.person.save',
        defaultMessage: 'Zapisz',
    },
    invalidPhoneNumber: {
        id: 'parishBook.person.invalidPhoneNumber',
        defaultMessage: 'Podaj numer telefonu w formacie międzynarodowym, np. +48123456789.',
    },
    invalidEmail: {
        id: 'parishBook.person.invalidEmail',
        defaultMessage: 'Niepoprawny e-mail.',
    },
    textTooLong: {
        id: 'parishBook.person.textTooLong',
        defaultMessage: 'Pole może mieć maksymalnie {max} znaków.',
    },
    descriptionTooLong: {
        id: 'parishBook.person.descriptionTooLong',
        defaultMessage: 'Opis może mieć maksymalnie {max} znaków.',
    },
});

export default ParishBookPersonForm;
